import { useState, useEffect } from "react";
import { ChecklistSkeleton } from "@/components/SkeletonLoaders";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardCheck } from "lucide-react";
import SectionEmptyState from "@/components/SectionEmptyState";

const VIOLATIONS = [
  { key: "cold-holding", label: "Cold holding temperature below 41°F for TCS foods" },
  { key: "hot-holding", label: "Hot holding temperature above 135°F for TCS foods" },
  { key: "handwashing", label: "Proper handwashing — soap, warm water, 20 seconds" },
  { key: "gloves", label: "Single-use gloves used properly and changed between tasks" },
  { key: "sanitizer-conc", label: "Sanitizer concentration at proper levels (quat or chlorine)" },
  { key: "food-labels", label: "All prepared foods labeled with date and name" },
  { key: "pest-control", label: "No signs of pests — droppings, nesting, live insects" },
  { key: "employee-health", label: "Employee illness policy posted and enforced" },
  { key: "thermometer", label: "Calibrated probe thermometer available and accurate" },
  { key: "cross-contam", label: "Raw meats stored below ready-to-eat foods" },
  { key: "cooling", label: "Proper cooling: 135°F to 70°F in 2 hrs, then to 41°F in 4 hrs" },
  { key: "reheating", label: "Reheating to 165°F within 2 hours" },
  { key: "dishwasher-temp", label: "Dishwasher reaching proper sanitizing temperature" },
  { key: "grease-trap", label: "Grease trap clean and properly maintained" },
  { key: "floor-drain", label: "Floor drains clean and free of standing water" },
  { key: "restroom", label: "Restrooms stocked with soap, paper towels, and self-closing doors" },
  { key: "food-contact", label: "Food contact surfaces cleaned and sanitized every 4 hours" },
  { key: "toxic-storage", label: "Chemicals stored separately from food and properly labeled" },
  { key: "fire-extinguisher", label: "Fire extinguisher inspected and accessible" },
  { key: "permit-display", label: "Current health permit and food handler certificates displayed" },
];

export default function HealthInspectionPrep() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const locId = activeLocation?.id || null;
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let q = supabase
      .from("health_inspection_checks")
      .select("item_key, checked")
      .eq("user_id", user.id);
    if (locId) q = q.eq("location_id", locId);
    q.then(({ data }) => {
      const map: Record<string, boolean> = {};
      (data || []).forEach((d: any) => { map[d.item_key] = d.checked; });
      setChecks(map);
      setLoading(false);
    });
  }, [user, locId]);

  const toggle = async (key: string) => {
    if (!user) return;
    const newVal = !checks[key];
    setChecks(prev => ({ ...prev, [key]: newVal }));

    await supabase.from("health_inspection_checks").upsert({
      user_id: user.id,
      item_key: key,
      checked: newVal,
      checked_at: newVal ? new Date().toISOString() : null,
      location_id: locId,
    }, { onConflict: "user_id,item_key" });
  };

  const checkedCount = Object.values(checks).filter(Boolean).length;
  const pct = Math.round((checkedCount / VIOLATIONS.length) * 100);
  const [started, setStarted] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-primary" /> Health Inspection Prep
          <span className="ml-auto text-sm font-normal text-muted-foreground">{checkedCount}/{VIOLATIONS.length} ({pct}%)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">The 20 most common health inspection violations. Work through each one before your next inspection to avoid costly citations.</p>
        {loading ? (
          <ChecklistSkeleton rows={6} />
        ) : checkedCount === 0 && !started ? (
          <SectionEmptyState
            icon={ClipboardCheck}
            title="Inspection prep not started"
            description="Walk through each item to make sure your kitchen, storage, and facilities are fully compliant."
            actionLabel="Begin Inspection Checklist"
            onAction={() => setStarted(true)}
          />
        ) : (
          <div className="space-y-2">
            {VIOLATIONS.map(v => (
              <label key={v.key} className="flex items-start gap-3 p-2 rounded hover:bg-muted/30 cursor-pointer">
                <Checkbox checked={!!checks[v.key]} onCheckedChange={() => toggle(v.key)} className="mt-0.5" />
                <span className={`text-sm ${checks[v.key] ? "line-through text-muted-foreground" : "text-foreground"}`}>{v.label}</span>
              </label>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
